import Box from '@mui/material/Box'
import { type EnrollmentData, NewEnrollmentStatus } from 'core/types'
import { Modal } from 'Components/Modal'
import { EnrollmentChip } from 'Components/Enrollment/EnrollmentChip'
import { ProgramVerificationChip } from 'Components/Program/ProgramVerificationChip'
import { ProgramTracker } from 'Components/Enrollment/ProgramTracker'
import { DatePickerWithLabel } from 'Components/DatePicker'
import { sendPost } from 'hooks/use-fetch'
import { toast } from 'react-toastify'
import { useState } from 'react'
import { useRefreshDispatch } from 'ContextProviders/RefreshProvider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'

interface Props {
  open: boolean
  enrollmentData: EnrollmentData
  onClose: () => void
}

const StartEnrollmentModal = (props: Props): JSX.Element => {
  const { open, enrollmentData, onClose } = props
  const [startDate, setStartDate] = useState<Date | null>(null)
  const disp = useRefreshDispatch()
  const refresh = (): void => { disp({ type: 'refresh' }) }

  const handleStartEnrollment = async (): Promise<void> => {
    if (startDate == null) {
      toast.error('Start Date is required')
      return
    }

    const { response } = await sendPost(`/NewEnrollment/StartEnrollment?id=${enrollmentData.id}`, { startDate })
    if (response.success === true) {
      toast.success(response.message)
      onClose()
      refresh()
    } else {
      response.errors.forEach((e: any) => toast.error(e.error))
    }
  }

  return <Modal
    onClose={onClose}
    open={open}
    onConfirm={handleStartEnrollment}
    title='Start Enrollment'
    confirmButtonText='Start Enrollment'
    cancelButtonText='Cancel'
    confirmationContent={
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant='body1' sx={{ fontWeight: 'bold' }}>{enrollmentData.providerName}</Typography>
        <ProgramVerificationChip programName={enrollmentData.programName} status={enrollmentData.programVerificationStatus} timeSpanName={enrollmentData.programTimeSpanName} showStatusName={true} />
        {enrollmentData.sessionId !== null && <ProgramTracker programId={enrollmentData.isdServiceProviderProgramId} sessionId={enrollmentData.sessionId} />}
        <Stack direction='column' spacing={0}>
          <Typography variant='subtitle2'>Facility</Typography>
          <Typography>{enrollmentData.facilityName ?? 'Not Selected'}</Typography>
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Stack direction='column' spacing={0}>
              <Typography variant='subtitle2'>Session</Typography>
              <Typography>{enrollmentData.sessionName ?? 'Not Selected'}</Typography>
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <Stack direction='column' spacing={0}>
              <Typography variant='subtitle2'>Delivery Schedule</Typography>
              <Typography>{enrollmentData.deliverySchedule ?? 'Not Selected'}</Typography>
            </Stack>
          </Grid>
        </Grid>
        <Box>
          <DatePickerWithLabel
            labelContent={<Typography variant='subtitle2'>Start Date</Typography>}
            value={startDate}
            onChange={(newDate) => { setStartDate(newDate) }}
            name={'startDate'}
          />
        </Box>
        <Stack direction='row' spacing={2}>
          <Typography>New Enrollment Status</Typography>
          <EnrollmentChip statusId={NewEnrollmentStatus.Started} />
        </Stack>
      </Box>
    }
  />
}

export default StartEnrollmentModal
